import { CollegeEligibilityFramework } from '../../../interfaces/EOAInterface';
import './TranscriptsCollegeEligibilityPage.css';

type TranscriptsCollegeEligibilityPageVariant = 'by-student' | 'by-subject-area';

interface TranscriptsCollegeEligibilityPageProps {
  frameworks: CollegeEligibilityFramework[];
  variant: TranscriptsCollegeEligibilityPageVariant;
}

const TranscriptsCollegeEligibilityPage = ({ frameworks, variant }: TranscriptsCollegeEligibilityPageProps) => {
  if (frameworks.length === 0) return null;

  const sortedFrameworks = [...frameworks].sort((a, b) => a.display_order - b.display_order);

  const mainFramework = sortedFrameworks.find((f) => f.is_main_requirement);
  const subjectOrder: string[] = mainFramework ? mainFramework.requirements.flatMap((r) => Object.keys(r)) : [];

  const seenSubjects = new Set(subjectOrder);
  for (const f of sortedFrameworks) {
    for (const req of f.requirements) {
      for (const subject of Object.keys(req)) {
        if (!seenSubjects.has(subject)) {
          seenSubjects.add(subject);
          subjectOrder.push(subject);
        }
      }
    }
  }

  const frameworkData = sortedFrameworks.reduce<Record<string, Record<string, string>>>((acc, f) => {
    acc[f.id] = Object.assign({}, ...f.requirements);
    return acc;
  }, {});

  const commonLocalUniversities = sortedFrameworks.filter((f) => f.is_common_local_university);
  const nonLocalColSpan = sortedFrameworks.length - commonLocalUniversities.length + 1;

  return (
    <div className={`college-eligibility-page college-eligibility-page--${variant} page`}>
      <div className="college-eligibility-header">
        <h2 className="college-eligibility-title">How do we define College Eligibility?</h2>
      </div>
      <p className="college-eligibility-intro">
        We define college eligibility using your state university system&apos;s high school course recommendations:
      </p>
      <div className="college-eligibility-table-wrapper">
        <table className="college-eligibility-table">
          <thead>
            {commonLocalUniversities.length > 0 && (
              <tr>
                <th className="col-empty" colSpan={nonLocalColSpan} />
                <th className="col-universities" colSpan={commonLocalUniversities.length}>
                  {commonLocalUniversities.length} Common Local{' '}
                  {commonLocalUniversities.length === 1 ? 'University' : 'Universities'}
                </th>
              </tr>
            )}
            <tr>
              <th className="col-header">Subject Requirement</th>
              {sortedFrameworks.map((f) => (
                <th key={f.id} className={`col-header${f.is_main_requirement ? ' col-eoaa' : ''}`}>
                  {f.college_name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {subjectOrder.map((subject) => (
              <tr key={subject}>
                <td>{subject}</td>
                {sortedFrameworks.map((f) => {
                  const value = frameworkData[f.id][subject] ?? '';
                  const newlineIndex = value.indexOf('\n');
                  const credits = newlineIndex !== -1 ? value.slice(0, newlineIndex) : value;
                  const note = newlineIndex !== -1 ? value.slice(newlineIndex + 1) : '';
                  return (
                    <td key={f.id} className={f.is_main_requirement ? 'col-eoaa' : ''}>
                      {credits}
                      {note && (
                        <>
                          <br />
                          <span className="col-note">{note}</span>
                        </>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TranscriptsCollegeEligibilityPage;
